import { AutoProcessor, AutoTokenizer, LlavaOnevisionForConditionalGeneration, RawImage } from '@huggingface/transformers';

// const model_id = 'Xenova/moondream2';
const model_id = 'llava-hf/llava-onevision-qwen2-0.5b-ov-hf';
let processor, tokenizer, model;

// =======================
// TTS 음성 목록 관리
// =======================
let voices = [];
let voicesReady = false;

function waitForIdle() {
  return new Promise((resolve) => {
    const check = () => {
      if (!speechSynthesis.speaking && !speechSynthesis.pending) {
        resolve();
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });
}


// 보이스 로딩을 보장하는 함수 (Promise 기반)
function loadVoices() {
  if (voicesReady && voices.length > 0) {
    return Promise.resolve(voices);
  }

  return new Promise((resolve) => {
    const synth = window.speechSynthesis;
    const v = synth.getVoices();

    if (v.length > 0) {
      voices = v;
      voicesReady = true;
      // console.log('[TTS] voices 즉시 로딩:', voices.length);
      resolve(voices);
      return;
    }

    const handler = () => {
      voices = synth.getVoices();
      voicesReady = true;
      synth.removeEventListener('voiceschanged', handler);
      // console.log('[TTS] voiceschanged 후 로딩:', voices.length);
      resolve(voices);
    };

    synth.addEventListener('voiceschanged', handler);
  });
}

// =======================
// TTS 상태 변수들
// =======================
let currentUtterance = null;
let queue = [];
let qIndex = 0;
let paused = false;

// 보호 상수
const RATE_MAX = 8;
const END_PAD = '   ';
const POST_GAP_MS = 120;
const FIRST_DELAY_MS = 120;     // cancel → speak 사이 딜레이
const FALLBACK_CHECK_MS = 800;  // 보이스 실패 감지 시간

// 현재 설정된 언어 (store에서 전달받음)
let currentSpeechLang = 'ko-KR';

// =======================
// 문장 분할 후 재생 시작
// language: 'ko-KR', 'en-US', 'ja-JP', 'ar-SA', 'fr-FR' 등
// =======================
export async function speakText(text, rate = 1, language = null) {
  if (!window.speechSynthesis) {
    console.error('[TTS] Web Speech API 미지원 브라우저입니다.');
    return;
  }

  // 언어 설정 (전달받은 값 또는 기본값)
  if (language) {
    currentSpeechLang = language;
  }

  console.log('[TTS] speakText 호출, language:', currentSpeechLang);

  // 이전 TTS 중단 후 브라우저 상태 안정화
  window.speechSynthesis.cancel();
  await waitForIdle();

  paused = false;
  currentUtterance = null;

  queue = text
    .split(/(?<=[\.!?])\s+|\n+/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (queue.length === 0) {
    console.warn('[TTS] speakText: 재생할 문장이 없습니다.');
    return;
  }

  qIndex = 0;

  loadVoices().then(async () => {
    await waitForIdle();
    playCurrent(rate, true);
  });
}

// 언어 설정 함수 (외부에서 호출)
export function setSpeechLanguage(langCode) {
  currentSpeechLang = langCode;
  console.log('[TTS] 언어 설정 변경:', currentSpeechLang);
}


// =======================
// 실제 한 문장 재생
// useFallback: 실패 시 default 보이스로 다시 시도할지 여부
// =======================
function playCurrent(rate, useFallback = true) {
  if (qIndex >= queue.length) {
    console.log('[TTS] 모든 문장 재생 완료');
    return;
  }

  const sentence = queue[qIndex];
  const u = new SpeechSynthesisUtterance(sentence + END_PAD);

  // 언어 코드에서 prefix 추출 (예: 'ko-KR' → 'ko')
  const langPrefix = currentSpeechLang.split('-')[0].toLowerCase();

  // 해당 언어 보이스 선택
  let selectedVoice = voices.find(
    (v) => v.lang && v.lang.toLowerCase().startsWith(langPrefix)
  );

  let mode = currentSpeechLang;

  if (selectedVoice) {
    console.log(`[TTS] ${currentSpeechLang} 보이스 선택:`, selectedVoice.name, selectedVoice.lang);
    u.voice = selectedVoice;
    u.lang = selectedVoice.lang;
  } else {
    mode = 'default-only';
    console.warn(`[TTS] ${currentSpeechLang} 보이스 없음 → 기본 보이스로 재생`);
    selectedVoice = voices.find((v) => v.default) || voices[0];
    if (selectedVoice) {
      console.log('[TTS] fallback 보이스:', selectedVoice.name, selectedVoice.lang);
      u.voice = selectedVoice;
      u.lang = selectedVoice.lang;
    }
  }

  u.rate = Math.min(rate, RATE_MAX);
  u.volume = 1;

  u.onstart = () => {};
  u.onend = () => {
    qIndex += 1;
    if (qIndex < queue.length) {
      setTimeout(() => playCurrent(rate, useFallback), POST_GAP_MS);
    }
  };
  u.onerror = (e) => {
    console.error('[TTS] onerror 발생:', e.error || e);
  };

  currentUtterance = u;
  window.speechSynthesis.speak(u);

  // 선택한 보이스가 "말을 안 하는" 경우 감지해서 fallback 시도
  if (mode !== 'default-only' && useFallback) {
    setTimeout(() => {
      const speaking = window.speechSynthesis.speaking;
      if (!speaking && currentUtterance === u) {
        console.warn(`[TTS] ${currentSpeechLang} 보이스 재생 실패 의심 → default 보이스로 재시도`);
        window.speechSynthesis.cancel();
        playWithDefaultVoice(sentence, rate);
      }
    }, FALLBACK_CHECK_MS);
  }
}

// =======================
// default 보이스로 바로 재생 (fallback 용)
// =======================
function playWithDefaultVoice(sentence, rate) {
  if (!voices.length) {
    console.error('[TTS] fallback: voices 없음');
    return;
  }

  const u = new SpeechSynthesisUtterance(sentence + END_PAD);
  const defaultVoice = voices.find((v) => v.default) || voices[0];

  // console.log('[TTS] fallback default 보이스 사용:', defaultVoice.name, defaultVoice.lang);

  u.voice = defaultVoice;
  u.lang = defaultVoice.lang;
  u.rate = Math.min(rate, RATE_MAX);
  u.volume = 1;

  u.onstart = () => {
    // console.log('[TTS] [fallback] onstart: 재생 시작');
  };
  u.onend = () => {
    // console.log('[TTS] [fallback] onend: 재생 완료');
    // 여기서도 다음 문장으로 진행
    qIndex += 1;
    if (qIndex < queue.length) {
      setTimeout(() => playCurrent(rate, /*useFallback*/ false), POST_GAP_MS);
    }
  };
  u.onerror = (e) => {
    console.error('[TTS] [fallback] onerror:', e.error || e);
  };

  currentUtterance = u;
  window.speechSynthesis.speak(u);
}

// =======================
// 컨트롤 함수들
// =======================
export function stopSpeaking() {
  queue = [];
  qIndex = 0;
  currentUtterance = null;
  paused = false;
  // console.log('[TTS] stopSpeaking 호출 → cancel()');
  window.speechSynthesis.cancel();
}

export function pauseSpeaking() {
  if (!paused && window.speechSynthesis.speaking) {
    window.speechSynthesis.pause();
    paused = true;
    // console.log('[TTS] pauseSpeaking');
  }
}

export function resumeSpeaking() {
  if (paused) {
    window.speechSynthesis.resume();
    paused = false;
    // console.log('[TTS] resumeSpeaking');
  }
}

export async function skipSentence(rate = 1) {
  if (!window.speechSynthesis.speaking) return;

  window.speechSynthesis.cancel();
  await waitForIdle();

  qIndex += 1;
  if (qIndex < queue.length) {
    playCurrent(rate, true);
  }
}

export async function replayPrevious(rate = 1) {
  if (!queue.length) return;

  window.speechSynthesis.cancel();
  await waitForIdle();

  qIndex = Math.max(0, qIndex - 1);
  playCurrent(rate, true);
}

export function isSpeaking() {
  return window.speechSynthesis.speaking;
}

export function isPaused() {
  return paused;
}

export function togglePlayPause(rate = 1) {
  if (paused || window.speechSynthesis.paused) {
    // console.log('[TTS] togglePlayPause → resume');
    resumeSpeaking();
    return 'resumed';
  }
  if (window.speechSynthesis.speaking) {
    // console.log('[TTS] togglePlayPause → pause');
    pauseSpeaking();
    return 'paused';
  }
  if (queue.length && qIndex < queue.length) {
    // console.log('[TTS] togglePlayPause → playCurrent from index', qIndex);
    playCurrent(rate, true);
    return 'started';
  }
  // console.log('[TTS] togglePlayPause → idle');
  return 'idle';
}

// 초기화: 미리 한 번 호출
loadVoices();
